import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { isEqual, entries, includes } from 'lodash';

import { isValidDomain } from '../../../../utilities/emailDomains/domainError';
import toggleInArray from '../../../../lib/toggleInArray';

import { DATE_TYPES, ATTACHMENT_TYPES, SIZE_TYPES, FORM_FIELDS } from '../config';

const BASE_KEY = 'components.dropdowns.filter';
const SEARCH_BASE_KEY = 'components.search';

export default function filterStateLogic() {
    return function FilterStateLogicComposer(component) {
        class FilterStateLogic extends Component {
            static propTypes = {
                originalFormValues: PropTypes.object.isRequired,
                formValues: PropTypes.object.isRequired,
                onFormChange: PropTypes.func.isRequired,
                getMessage: PropTypes.func.isRequired,
                disabledFields: PropTypes.array,
                parseSearchStringValue: PropTypes.func.isRequired,
            };

            static defaultProps = {
                disabledFields: [],
            };

            onFormChange = (formField, fieldValue) => (newValue) => {
                const { formValues } = this.props;
                const newFieldValues = { [fieldValue]: newValue };

                // Add localized values
                if (formField === FORM_FIELDS.ATTACHMENTS) {
                    const localizedData = this.getLocalizedData(FORM_FIELDS.ATTACHMENTS, ATTACHMENT_TYPES);

                    newFieldValues.localizedValue =
            (localizedData.find(data => data.id === newValue) || {}).value || '';
                }

                if (formField === FORM_FIELDS.SIZE) {
                    const localizedData = this.getLocalizedData(FORM_FIELDS.SIZE, SIZE_TYPES);

                    newFieldValues.localizedValue = newValue.map(value => (localizedData.find(data => data.id === value) || {}).value || '',);
                }

                if (formField === FORM_FIELDS.DATE && fieldValue === 'type') {
                    const localizedData = this.getLocalizedData(FORM_FIELDS.DATE, DATE_TYPES);

                    newFieldValues.localizedValue =
            (localizedData.find(data => data.id === newValue) || {}).value || '';

                    // validate date values if changed
                    if (newValue !== DATE_TYPES.RANGE) {
                        newFieldValues.to = '';
                    }

                    if (!newValue.length) {
                        newFieldValues.from = '';
                    }
                }

                this.props.onFormChange({
                    ...formValues,
                    [formField]: {
                        ...formValues[formField],
                        ...newFieldValues,
                    },
                });
            };

            onMultiSelectChange = (field, value) => (selectedValue) => {
                const { formValues } = this.props;
                const currentValues = formValues[field] ? formValues[field][value] : [];

                this.onFormChange(field, value)(toggleInArray(currentValues || [], selectedValue));
            };

            onInputChange = (field, value) => {
                this.onFormChange(field, 'query')(value);
            };

            onDeleteItem = (formField, deletedId) => {
                const { formValues } = this.props;

                this.onFormChange(formField, 'items')(formValues[formField].items.filter(({ id }) => id !== deletedId),);
            };

            getLocalizedData = (type, dataTypes) => Object.keys(dataTypes).map(key => ({
                id: dataTypes[key],
                value: this.props.getMessage(`${BASE_KEY}.${type}.${dataTypes[key]}`),
            }));

            get isRangeDate() {
                return (this.props.formValues[FORM_FIELDS.DATE] || {}).type === DATE_TYPES.RANGE;
            }

            get isDirtyForm() {
                return !isEqual(this.props.originalFormValues, this.props.formValues);
            }

            get isValidForm() {
                const { formValues, parseSearchStringValue } = this.props;
                return Object.keys(formValues).every((formElement) => {
                    // domain search sanitation
                    if (formElement === FORM_FIELDS.PARTICIPANTS_DOMAINS && formValues[formElement].query) {
                        return this.isDomainSearchValid(
                            formValues,
                            FORM_FIELDS.PARTICIPANTS,
                            parseSearchStringValue,
                        );
                    }

                    if (formElement === FORM_FIELDS.SENDER_DOMAINS && formValues[formElement].query) {
                        return this.isDomainSearchValid(
                            formValues,
                            FORM_FIELDS.SENDER,
                            parseSearchStringValue,
                        );
                    }
                    // end domain search sanitation

                    if (formElement !== FORM_FIELDS.DATE || !formValues[formElement].type.length) {
                        return true;
                    }

                    const { type = '', from = '', to = '' } = formValues[formElement];

                    if (
                        (includes([DATE_TYPES.AFTER, DATE_TYPES.BEFORE, DATE_TYPES.SPECIFIC], type) &&
              !!from.length) ||
            (DATE_TYPES.RANGE && !!from.length && !!to.length)
                    ) {
                        return true;
                    }

                    return false;
                });
            }

            get isFormHasValues() {
                return entries(this.props.formValues).some(formElement => Object.keys(formElement[1]).some(value => !!formElement[1][value].length),);
            }

            get formValues() {
                return Object.keys(FORM_FIELDS).reduce(
                    (all, field) => ({
                        ...all,
                        [FORM_FIELDS[field]]: this.props.formValues[FORM_FIELDS[field]] || {},
                    }),
                    {},
                );
            }

            // queryField: sender or participant
            // domainsFields: sender_domains or participant_domains
            getDomainsErrorText = (formValues, queryField, domainsField) => {
                if (formValues[domainsField].query) {
                    const field = formValues[queryField] && formValues[queryField].query;
                    if (!field || field.length === 0) return null; // no text in input field
                    if (field.includes('@') || field.includes('*')) { // domain string is using * or @
                        return `${SEARCH_BASE_KEY}.domains.char_error`;
                    }
                    if (
                        formValues[queryField].items &&
            formValues[queryField].items.length > 0
                    ) { // is using lists
                        return `${SEARCH_BASE_KEY}.domains.list_error`;
                    }
                }
                return null;
            };

            // used to show the helper message for domain fields
            containsInvalidDomain = (field, domainsField, parseSearchStringValue) => {
                if (domainsField) {
                    if (!field) return false;
                    const arrOfDomains = parseSearchStringValue(field);
                    const invalidDomains = arrOfDomains.some(domain => !isValidDomain(domain));
                    return invalidDomains;
                }
                return false;
            };

            // used to disable the search button
            isDomainSearchValid = (formValues, key, parseSearchStringValue) => {
                const field =
          formValues[key] && formValues[key].query;
                if (!field || field.length === 0) return false;
                const containsWildcard = field.includes('*');
                const arrOfDomains = parseSearchStringValue(field);
                const allAreValidDomains = arrOfDomains.every(domain => isValidDomain(domain));
                const usingLists = formValues[key].items && formValues[key].items.length > 0;
                const isValid = !containsWildcard && allAreValidDomains && !usingLists;
                return isValid;
            };

            isFieldEnabled = field => !includes(this.props.disabledFields, field);

            render() {
                return React.createElement(component, {
                    ...this.props,
                    ...this.state,
                    onFormChange: this.onFormChange,
                    onInputChange: this.onInputChange,
                    onMultiSelectChange: this.onMultiSelectChange,
                    onDeleteItem: this.onDeleteItem,
                    getLocalizedData: this.getLocalizedData,
                    isRangeDate: this.isRangeDate,
                    isDirtyForm: this.isDirtyForm,
                    isValidForm: this.isValidForm,
                    isFormHasValues: this.isFormHasValues,
                    formValues: this.formValues,
                    disabledFields: this.disabledFields,
                    isFieldEnabled: this.isFieldEnabled,
                    containsInvalidDomain: this.containsInvalidDomain,
                    getDomainsErrorText: this.getDomainsErrorText,
                });
            }
        }

        return FilterStateLogic;
    };
}
